import {
    Customer,
    DateFields,
    Fare,
    JourneyMapType,
    OriginDestinationInfo,
    SelectedAncillaryType,
    TaxDetails,
    Traveler,
    UserSelectedBaggageDetail,
    UserSelectedMealDetail,
    UserSelectedSeatDetail,
    VasAncillary
} from '@/src/types/itinerary'
import { isEmpty } from '../generalUtils/isEmpty'
import { logError } from '../logError'
import { dateObjectToIsoString } from './dateTransformer'
import { getFareBrandDetails } from './fareFamily'
import { titleToGenderMap } from './genderToTitleMap'

export const getReviewItineraryPayload = ({
    itineraryId,
    selectedInsurances,
    selectedFare
}: {
    itineraryId: string
    selectedInsurances: VasAncillary[]
    selectedFare: Fare
}) => {
    const payload: {
        addVasRequest: {
            selectedVas: {
                vasType: string
                provider: string
                noOfDays?: number
            }[]
        }
        itineraryId: string
        type: string[]
    } = {
        addVasRequest: {
            selectedVas: []
        },
        itineraryId,
        type: ['VAS']
    }
    if (Array.isArray(selectedInsurances)) {
        selectedInsurances.forEach(insurance => {
            const { vasType, vasProvider: provider } = insurance || {}
            payload.addVasRequest.selectedVas.push({
                vasType,
                provider,
                noOfDays: 1
            })
        })
    }
    const { fareMetaMessages = [] } = selectedFare || {}
    if (!isEmpty(fareMetaMessages?.[0])) {
        const { category, provider } = fareMetaMessages[0]
        payload.addVasRequest.selectedVas.push({
            vasType: category,
            provider
        })
    } else {
        payload.addVasRequest.selectedVas.push({
            provider: 'Airline',
            vasType: 'Standard fare'
        })
    }

    return payload
    // if (!isEmpty(shukranData)) {
    //     payload.shukranCardRequest = shukranData
    //     payload.type.push('SHUKRAN')
    // }
}

export const getTravelerDetailsPayload = (props: {
    travelers: Traveler[]
    itineraryId: string
    contactDetails: Customer
    isDateOfBirthRequired: boolean
    isNationalityRequiredToRenderInUserPersonalDetailsSection: boolean
    taxDetails: TaxDetails
    isBirthCountryRequired: boolean
}) => {
    const payload = {
        addVatRequest: props.taxDetails.addTaxChecked
            ? {
                  holderAddress: '',
                  holderName: props.taxDetails.companyName,
                  registrationNumber: props.taxDetails.taxNo
              }
            : undefined,
        itineraryId: props.itineraryId,
        type: props.taxDetails.addTaxChecked ? ['USER_DETAILS', 'VAT'] : ['USER_DETAILS'],
        userDetailsRequest: {
            customer: props.contactDetails,
            userDetails: {
                username: props.contactDetails.email,
                id: props.contactDetails.peopleId,
                travellerDetails: props.travelers.map(traveler => {
                    const doc = traveler.documentDetails?.[traveler.selectedDocumentType!]
                    return {
                        id: traveler.id ?? null,
                        docDetails: doc
                            ? [
                                  {
                                      docType: doc.docType,
                                      docNumber: doc.docNumber,
                                      id: doc.id ?? null,
                                      countryIssued: doc.countryIssued,
                                      expiryDate: doc.expiryDate
                                          ? dateObjectToIsoString(doc.expiryDate as DateFields)
                                          : null,
                                      nationality: doc.nationality,
                                      dateOfBirth: props.isDateOfBirthRequired
                                          ? dateObjectToIsoString(traveler.dateOfBirth as DateFields)
                                          : null
                                  }
                              ]
                            : [],
                        personalDetails: {
                            firstName: traveler.firstName,
                            lastName: traveler.lastName,
                            title: traveler.title,
                            gender: titleToGenderMap(traveler.title),
                            paxType: traveler.paxType,
                            id: traveler.id ?? null,
                            dateOfBirth: props.isDateOfBirthRequired
                                ? dateObjectToIsoString(traveler.dateOfBirth as DateFields)
                                : null,
                            nationality: props.isNationalityRequiredToRenderInUserPersonalDetailsSection
                                ? traveler.nationality
                                : null,
                            birthCountry: props.isBirthCountryRequired ? traveler.birthCountry : null
                        }
                    }
                })
            }
        }
    }

    return payload
}
export const getAncillaryUpdatePayload = ({
    journeyMap,
    selectedAncillaries
}: {
    journeyMap: JourneyMapType
    selectedAncillaries: SelectedAncillaryType[]
}) => {
    try {
        const payload: {
            userSelectedJourneyAncillaries: {
                journeyOriginDestinationInfo: OriginDestinationInfo
                userSelectedBaggageAncillaries: {
                    originDestinationInfo: OriginDestinationInfo
                    baggageDetails: Partial<UserSelectedBaggageDetail>[]
                }[]
                userSelectedSegmentAncillaries: {
                    segmentOriginDestinationInfo: OriginDestinationInfo
                    mealDetails: UserSelectedMealDetail[]
                    seatDetails: UserSelectedSeatDetail[]
                }[]
            }[]
        } = {
            userSelectedJourneyAncillaries: []
        }

        // Add safety check for journeyAncillaries
        if (!Array.isArray(journeyMap)) {
            return payload
        }

        journeyMap.forEach(journeyAncillary => {
            // Add safety checks for undefined values
            if (!journeyAncillary) return

            const { baggageAncillary, segmentAncillaries = [], originDestinationInfo } = journeyAncillary
            const { baggageDetails = [] } = baggageAncillary || {}

            const journeyObject: {
                journeyOriginDestinationInfo: OriginDestinationInfo
                userSelectedBaggageAncillaries: {
                    originDestinationInfo: OriginDestinationInfo
                    baggageDetails: Partial<UserSelectedBaggageDetail>[]
                }[]
                userSelectedSegmentAncillaries: {
                    segmentOriginDestinationInfo: OriginDestinationInfo
                    mealDetails: UserSelectedMealDetail[]
                    seatDetails: UserSelectedSeatDetail[]
                }[]
            } = {
                journeyOriginDestinationInfo: originDestinationInfo,
                userSelectedBaggageAncillaries: [],
                userSelectedSegmentAncillaries: []
            }

            // Process segment ancillaries
            if (Array.isArray(segmentAncillaries)) {
                segmentAncillaries.forEach(segmentAncillary => {
                    const { originDestinationInfo: segmentOriginDestinationInfo } = segmentAncillary
                    const { from: segmentFrom, to: segmentTo } = segmentOriginDestinationInfo || {}
                    if (!segmentFrom || !segmentTo) return

                    const selectedAncillary = selectedAncillaries?.find(ancillary => {
                        const { from, to } = ancillary?.segmentOriginDestinationInfo || {}
                        return from === segmentFrom && to === segmentTo
                    })

                    const { mealDetails = [], seatDetails = [] } = selectedAncillary || {}

                    const segmentObject: {
                        segmentOriginDestinationInfo: OriginDestinationInfo
                        mealDetails: UserSelectedMealDetail[]
                        seatDetails: UserSelectedSeatDetail[]
                    } = {
                        segmentOriginDestinationInfo,
                        mealDetails: [],
                        seatDetails: []
                    }

                    if (Array.isArray(mealDetails)) {
                        segmentObject.mealDetails =
                            mealDetails.map(
                                (meal, index): UserSelectedMealDetail => ({
                                    mealCode: meal?.code || '',
                                    mealInfo: meal?.description || '',
                                    paxIndex: index + 1
                                })
                            ) || []
                    }

                    if (Array.isArray(seatDetails)) {
                        segmentObject.seatDetails =
                            seatDetails.map(
                                (seat, index): UserSelectedSeatDetail => ({
                                    columnId: seat.columnId || '',
                                    rowId: seat.rowId || '',
                                    paxIndex: index + 1
                                })
                            ) || []
                    }
                    journeyObject.userSelectedSegmentAncillaries.push(segmentObject)
                })
            }

            // Process baggage details separately for userSelectedBaggageAncillaries
            if (Array.isArray(baggageDetails)) {
                baggageDetails.forEach(baggageDetail => {
                    if (!baggageDetail) return

                    const { originDestinationInfo: segmentOriginDestinationInfo } = baggageDetail
                    const { from: baggageFrom, to: baggageTo } = segmentOriginDestinationInfo || {}

                    if (!baggageFrom || !baggageTo) return

                    const selectedAncillary = selectedAncillaries?.find(ancillary => {
                        const { from, to } = ancillary?.segmentOriginDestinationInfo || {}
                        return from === baggageFrom && to === baggageTo
                    })

                    const { baggageDetails: selectedBaggageDetails = [] } = selectedAncillary || {}

                    const baggageObject: {
                        originDestinationInfo: OriginDestinationInfo
                        baggageDetails: Partial<UserSelectedBaggageDetail>[]
                    } = {
                        originDestinationInfo,
                        baggageDetails: []
                    }
                    selectedBaggageDetails?.forEach((baggage, index) => {
                        baggageObject.baggageDetails.push({
                            baggageCode: baggage.baggageCode || '',
                            baggageInfo: baggage.displayDetail || '',
                            inclusiveFreeBaggage: baggage.isInclusiveFreeBaggage || false,
                            displayDetail: baggage.displayDetail || '',
                            paxIndex: index + 1
                        })
                    })
                    journeyObject.userSelectedBaggageAncillaries.push(baggageObject)
                })
            }
            payload.userSelectedJourneyAncillaries.push(journeyObject)
        })

        return payload
    } catch (error) {
        logError('getAncillaryUpdatePayload', error)
        return {
            userSelectedJourneyAncillaries: []
        }
    }
}

export const selectFareFamilyPayload = ({ itineraryId, selectedFare }: { itineraryId: string; selectedFare: Fare }) => {
    const { ctBrandName = '' } = getFareBrandDetails(selectedFare)

    const payload = {
        fareSelected: {
            fareType: ctBrandName
        },
        addVasRequest: { selectedVas: [] },
        itineraryId,
        type: ['FARE_SELECTION']
    }

    return payload
}
